// SatelliteInteraction — global raycaster + state bus for the
// satellites + note fragments. Runs in useFrame and keeps
// window.__planetHover in sync with what the mouse is currently over.
//
// The actual pointer events (over/out/click) are wired on the meshes
// themselves via R3F's onPointerOver/Out/Click (see Satellites.tsx and
// NoteFragments.tsx). Those fire R3F's internal raycaster. This module
// adds a *secondary* CPU raycaster that:
//   1) Verifies and reconciles hover state (R3F's events can drop if
//      the user drags off a mesh; the raycaster catches the case).
//   2) Reads the camera + mouse position once per frame, raycasts
//      against the union of satellite + fragment meshes, and writes
//      the result to window.__planetHover.
//   3) Auto-clears stale hover after a brief inactivity window.
//
// Performance: the raycaster is configured with a 4-pixel radius so we
// don't have to be perfectly pixel-accurate. The mesh list is built
// once at mount (memoized) and reused every frame. If the scene has
// no interactive meshes (e.g. before Satellites/NoteFragments mount),
// the raycast early-outs in O(1).

import { useEffect, useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import type { Camera } from 'three';

import { SATELLITES, NOTES } from './planetSections';
import { onDeepDiveOpen } from '../utils/analytics';

const RAYCAST_RADIUS_PX = 4;
const HOVER_CLEAR_DELAY_MS = 80;

// ---------------------------------------------------------------------------
// Tiny utility: walk a group's descendants and pick the first Mesh with
// userData.kind set. We don't traverse Three.js's full Mesh.raycast
// machinery — we instead build an explicit list at mount so the per-frame
// raycast is a tight loop over a known number of targets.
// ---------------------------------------------------------------------------

interface PickableMesh {
  mesh: THREE.Mesh;
  kind: 'satellite' | 'note';
  id: string | number;
}

function collectPickables(): PickableMesh[] {
  if (typeof window === 'undefined') return [];
  const w = window as Window & { __planetPickables?: PickableMesh[] };

  // The satellites and note-fragments groups register their meshes via
  // window.__planetPickables when they mount. This avoids a full scene
  // traversal every frame.
  const list = w.__planetPickables ?? [];
  return list.slice();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface SatelliteInteractionProps {
  /** When false, the raycaster is suspended (e.g. on the Wake scene). */
  enabled?: boolean;
}

export function SatelliteInteraction({ enabled = true }: SatelliteInteractionProps) {
  const camera = useThree((s) => s.camera) as Camera;
  const gl = useThree((s) => s.gl) as THREE.WebGLRenderer;
  const raycaster = useMemo(() => {
    const r = new THREE.Raycaster();
    r.params.Mesh = { threshold: 0 };
    r.params.Points = { threshold: 1 };
    return r;
  }, []);

  // Per-frame scratch
  const ndc = useRef(new THREE.Vector2());
  const mouseClient = useRef<{ x: number; y: number; active: boolean }>({
    x: 0,
    y: 0,
    active: false,
  });
  const lastHoverTime = useRef(0);
  const pickablesRef = useRef<PickableMesh[]>([]);

  // -------------------------------------------------------------------------
  // Track mouse position via DOM events (cheaper than reading clientX
  // every frame from R3F's pointer state).
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onMove = (e: PointerEvent) => {
      mouseClient.current.x = e.clientX;
      mouseClient.current.y = e.clientY;
      mouseClient.current.active = true;
    };
    const onLeave = () => {
      mouseClient.current.active = false;
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerleave', onLeave);
    };
  }, []);

  // -------------------------------------------------------------------------
  // Refresh the pickables list every frame. If the array on window is
  // stale (Satellites/NoteFragments haven't mounted yet), the loop is
  // O(0).
  // -------------------------------------------------------------------------

  useFrame(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;
    if (!mouseClient.current.active) return;

    // Refresh pickables (cheap: array slice + length check).
    const pickables = collectPickables();
    if (pickables.length === 0) {
      pickablesRef.current = [];
      // Nothing to raycast — but we still want to clear stale hover
      // if the mouse just left the window.
      return;
    }
    if (pickablesRef.current !== pickables) {
      pickablesRef.current = pickables;
    }

    // Convert mouse client coords → NDC.
    const rect = gl.domElement.getBoundingClientRect();
    ndc.current.x =
      ((mouseClient.current.x - rect.left) / rect.width) * 2 - 1;
    ndc.current.y =
      -(((mouseClient.current.y - rect.top) / rect.height) * 2 - 1);

    raycaster.setFromCamera(ndc.current, camera);

    // Test each pickable individually (faster than full scene
    // intersect since the list is tiny: 4 + 5 = 9 meshes).
    let hit: PickableMesh | null = null;
    let bestDist = Infinity;
    for (let i = 0; i < pickables.length; i++) {
      const p = pickables[i];
      if (!p.mesh || !p.mesh.geometry) continue;
      // Cheap cull: behind the camera.
      const worldPos = new THREE.Vector3();
      p.mesh.getWorldPosition(worldPos);
      const dx = worldPos.x - camera.position.x;
      const dy = worldPos.y - camera.position.y;
      const dz = worldPos.z - camera.position.z;
      const distSq = dx * dx + dy * dy + dz * dz;
      if (distSq < 0.01) continue;
      // Forward distance from camera plane.
      const camForward = new THREE.Vector3();
      camera.getWorldDirection(camForward);
      const forwardDist =
        dx * camForward.x + dy * camForward.y + dz * camForward.z;
      if (forwardDist < 0) continue;

      // Do the actual ray-mesh test.
      const intersects = raycaster.intersectObject(p.mesh, false);
      if (intersects.length > 0) {
        const d = intersects[0].distance;
        if (d < bestDist) {
          bestDist = d;
          hit = p;
        }
      }
    }

    if (hit) {
      lastHoverTime.current = performance.now();
      const cur = window.__planetHover;
      const same =
        cur &&
        ((cur.kind === 'satellite' && hit!.kind === 'satellite' && cur.id === hit!.id) ||
          (cur.kind === 'note' && hit!.kind === 'note' && cur.id === hit!.id));
      if (!same) {
        window.__planetHover = {
          kind: hit.kind,
          id: hit.id,
        } as Window['__planetHover'];
      }
    } else {
      // Brief grace period before clearing — avoids flicker when the
      // mouse passes over the gap between two meshes.
      if (performance.now() - lastHoverTime.current > HOVER_CLEAR_DELAY_MS) {
        if (window.__planetHover) {
          window.__planetHover = null;
        }
      }
    }
  });

  return null;
}

// ---------------------------------------------------------------------------
// Public helper — Satellites.tsx and NoteFragments.tsx call this from
// their mount effect to register their meshes with the global pickables
// list. Keeping registration centralized here means the raycaster
// doesn't need to know about mesh refs.
// ---------------------------------------------------------------------------

export function registerPickables(
  meshes: { mesh: THREE.Mesh; kind: 'satellite' | 'note'; id: string | number }[],
): () => void {
  if (typeof window === 'undefined') return () => {};
  const w = window as Window & { __planetPickables?: PickableMesh[] };
  const list = (w.__planetPickables ?? []) as PickableMesh[];
  // Replace any prior entries for the same kind+id.
  const filtered = list.filter(
    (p) => !meshes.some((m) => m.kind === p.kind && m.id === p.id),
  );
  filtered.push(...meshes);
  w.__planetPickables = filtered;

  // Suppress unused-import warning by referencing constants in dev.
  void SATELLITES;
  void NOTES;

  return () => {
    const cur = (w.__planetPickables ?? []) as PickableMesh[];
    w.__planetPickables = cur.filter(
      (p) => !meshes.some((m) => m.kind === p.kind && m.id === p.id),
    );
  };
}

// ---------------------------------------------------------------------------
// Public helper — write the open-panel value to the global state and
// dispatch the event. Used by Satellites, NoteFragments, SceneLandmarks.
// Defined here so there's a single import path for click handlers.
// ---------------------------------------------------------------------------

/** Detail panel open shape. Re-exported by DetailPanel. */
export interface OpenPanelValue {
  type: 'project' | 'note' | 'milestone' | 'about' | 'skills' | 'contact';
  id: string;
}

/**
 * Open the deep-dive detail panel. Writes to window.__planetOpenPanel
 * and dispatches the 'planet:detail:open' event the DetailPanel listens
 * to. Safe to call from anywhere — including R3F event handlers that
 * fire outside of a React render tree.
 */
export function openDetail(value: OpenPanelValue | null): void {
  if (typeof window === 'undefined') return;
  (window as Window & { __planetOpenPanel?: OpenPanelValue | null }).__planetOpenPanel = value;
  window.dispatchEvent(
    new CustomEvent('planet:detail:open', { detail: value }),
  );
  // Analytics — count every deep-dive open. We treat null (close) as
  // a no-op so we can be safely called for closures too.
  if (value) onDeepDiveOpen(value.type, value.id);
}

export default SatelliteInteraction;